import React, { useState } from 'react';
import { auth } from '../services/auth';

interface Props {
  onLoggedIn: () => void;
}

export const LoginScreen: React.FC<Props> = ({ onLoggedIn }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.login(email.trim(), password);
      onLoggedIn();
    } catch (err: any) {
      setError(err?.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <div style={styles.iconBox}>
          <div style={styles.iconCircle}>TH</div>
        </div>
        <h1 style={styles.title}>Sign in to Artemis Template Management</h1>
        <p style={styles.subtitle}>Use your account to continue.</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>Email</label>
          <input
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            required
            style={styles.input}
          />

          <label style={styles.label}>Password</label>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            required
            style={styles.input}
          />

          {error && <div style={styles.error}>{error}</div>}

          <button
            type="submit"
            disabled={submitting || !email || !password}
            style={{ ...styles.button, opacity: submitting || !email || !password ? 0.6 : 1 }}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: 16,
    background: '#f8fafc',
    fontFamily: 'Segoe UI, system-ui, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 360,
    background: 'white',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    padding: 24,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  iconBox: { display: 'flex', justifyContent: 'flex-start', marginBottom: 12 },
  iconCircle: {
    width: 40, height: 40, borderRadius: 8,
    background: '#0d9488', color: 'white', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontWeight: 600,
  },
  title: { fontSize: 18, fontWeight: 600, color: '#0f172a', margin: '0 0 4px 0' },
  subtitle: { fontSize: 13, color: '#64748b', margin: '0 0 16px 0' },
  form: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 500, color: '#334155', marginTop: 8 },
  input: {
    padding: '8px 10px',
    fontSize: 14,
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    outline: 'none',
  },
  error: {
    marginTop: 8,
    padding: '6px 10px',
    fontSize: 12,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    borderRadius: 6,
  },
  button: {
    marginTop: 16,
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 500,
    background: '#0d9488',
    color: 'white',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  testHint: {
    marginTop: 20, paddingTop: 16,
    borderTop: '1px solid #e2e8f0',
    fontSize: 11, color: '#64748b', lineHeight: 1.6,
  },
  testHintTitle: { fontWeight: 600, color: '#475569', marginBottom: 4 },
};
